import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { PermissionSnapshot } from '../models/position.model';
import {
  canEditFeature,
  canViewFeature,
  canViewField,
  defaultFeaturesFor,
  EDIT_ROLES,
  FIELD_ROLES,
  PERM_CACHE_KEY,
  readPermissionSnapshot,
  ROUTE_ROLES,
} from '../guards/route-roles';
import { PositionService } from './position/position.service';

/**
 * Quyền của tài khoản đang đăng nhập.
 *
 * Tải 1 lần lúc đăng nhập (và lúc guard thấy cache trống sau khi F5), rồi cache
 * vào localStorage để `canViewFeature`/`canEditFeature` đọc đồng bộ ngay trong
 * template mà không phải subscribe.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private positionService: PositionService) {}

  /** Quyền đang cache (null nếu chưa tải). */
  get snapshot(): PermissionSnapshot | null {
    return readPermissionSnapshot();
  }

  canView(feature: string): boolean {
    return canViewFeature(feature);
  }

  canEdit(feature: string): boolean {
    return canEditFeature(feature);
  }

  /** Có được xem trường nhạy cảm này không ('cost-price' | 'profit'). */
  canViewField(field: string): boolean {
    return canViewField(field);
  }

  /**
   * Tải quyền của 1 vị trí từ Firestore rồi cache lại.
   * Vị trí không tồn tại / đã xoá → rơi về bảng mặc định trong route-roles.ts
   * (vẫn đúng với 3 vị trí gốc, và là rỗng với vị trí lạ).
   */
  async load(positionId: string | null | undefined): Promise<PermissionSnapshot> {
    const id = (positionId ?? '').toString();
    let snap = this.defaultSnapshot(id);
    if (id) {
      try {
        const position = await this.positionService.getById(id);
        if (position && !position.deleted) {
          snap = {
            position_id: id,
            position_name: position.name || '',
            is_admin: !!position.is_admin,
            view: position.view || [],
            edit: position.edit || [],
            field: position.field || [],
          };
        }
      } catch (err) {
        // Mất mạng / bị rules chặn: giữ bản mặc định, không chặn đăng nhập.
        console.error('permission.load error:', err);
      }
    }
    this.write(snap);
    return snap;
  }

  /** Có cache thì dùng luôn, chưa có thì tải theo role trong localStorage. */
  ensureLoaded(): Observable<PermissionSnapshot> {
    const cached = this.snapshot;
    if (cached) return of(cached);
    const role = localStorage.getItem('role');
    return from(this.load(role)).pipe(
      catchError(() => of(this.defaultSnapshot((role ?? '').toString())))
    );
  }

  /** Tải lại quyền cho phiên hiện tại (gọi sau khi Admin sửa ma trận). */
  refresh(): Observable<PermissionSnapshot> {
    return from(this.load(localStorage.getItem('role')));
  }

  /** Dùng cho guard: vị trí hiện tại có mở được feature này không. */
  canViewAsync(feature: string): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map((snap) => snap.is_admin || snap.view.indexOf(feature) !== -1)
    );
  }

  clear(): void {
    localStorage.removeItem(PERM_CACHE_KEY);
  }

  private write(snap: PermissionSnapshot): void {
    try {
      localStorage.setItem(PERM_CACHE_KEY, JSON.stringify(snap));
    } catch (err) {
      console.error('permission cache error:', err);
    }
  }

  /** Bản dự phòng suy từ bảng mặc định — vị trí lạ sẽ ra rỗng. */
  private defaultSnapshot(id: string): PermissionSnapshot {
    return {
      position_id: id,
      position_name: '',
      is_admin: false,
      view: defaultFeaturesFor(id, ROUTE_ROLES),
      edit: defaultFeaturesFor(id, EDIT_ROLES),
      field: defaultFeaturesFor(id, FIELD_ROLES),
    };
  }
}
